/* WoofSocks native line-item photos. No third-party upload service. */
(() => {
  if (customElements.get('woofsocks-photo-upload')) return;
  const drafts = new Map();
  const MAX_FILES = 4;
  const MAX_BYTES = 10 * 1024 * 1024; // Our chosen per-file limit, not a Shopify platform claim.
  const allowed = /\.(jpe?g|png|webp)$/i;

  class PhotoUpload extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.key = this.dataset.productId;
      this.state = drafts.get(this.key) || { photos: [], busy: false };
      drafts.set(this.key, this.state);
      this.picker = this.querySelector('[data-photo-picker]');
      this.list = this.querySelector('[data-photo-list]');
      this.error = this.querySelector('[data-photo-error]');
      this.status = this.querySelector('[data-photo-status]');
      this.button = this.querySelector('[data-photo-choose]');
      this.button.addEventListener('click', () => this.picker.click());
      this.picker.addEventListener('change', () => {
        this.addFiles([...this.picker.files]);
        this.picker.value = '';
      });
      const zone = this.querySelector('[data-photo-dropzone]');
      zone.addEventListener('dragover', event => { event.preventDefault(); zone.classList.add('is-dragging'); });
      zone.addEventListener('dragleave', () => zone.classList.remove('is-dragging'));
      zone.addEventListener('drop', event => {
        event.preventDefault(); zone.classList.remove('is-dragging');
        this.addFiles([...event.dataTransfer.files]);
      });
      this.render();
    }

    get form() { return document.getElementById(this.dataset.formId); }
    get current() {
      return this.isConnected ? this : [...document.querySelectorAll('woofsocks-photo-upload')]
        .find(element => element.dataset.formId === this.dataset.formId && element.state === this.state) || this;
    }

    addFiles(files) {
      if (this.state.busy) return;
      this.showError('');
      if (files.length + this.state.photos.length > MAX_FILES) {
        this.showError('You can add up to 4 photos per pair. Remove a photo before adding another.'); return;
      }
      for (const file of files) {
        if (!allowed.test(file.name) || (file.type && !/^image\//i.test(file.type))) {
          this.showError('Please choose JPG, PNG or WebP photos. Export HEIC photos as JPG first.'); return;
        }
        if (!file.size || file.size > MAX_BYTES) {
          this.showError('Each photo must be between 1 byte and 10 MB. Please choose a smaller photo.'); return;
        }
      }
      files.forEach(file => this.state.photos.push({ file, note: '', url: URL.createObjectURL(file) }));
      this.render();
    }

    showError(message) {
      if (this.current !== this) return this.current.showError(message);
      this.error.textContent = message;
      this.error.hidden = !message;
      this.button.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    render() {
      this.list.replaceChildren();
      this.state.photos.forEach((photo, index) => {
        const card = document.createElement('li'); card.className = 'ws-native-photo__card';
        const image = document.createElement('img'); image.src = photo.url;
        image.alt = `Selected pet photo ${index + 1}`;
        image.addEventListener('error', () => { image.hidden = true; });
        const info = document.createElement('div'); info.className = 'ws-native-photo__info';
        const name = document.createElement('span'); name.className = 'ws-native-photo__filename'; name.textContent = photo.file.name;
        const label = document.createElement('label'); label.textContent = `Notes for photo ${index + 1} (optional)`;
        const note = document.createElement('textarea'); note.rows = 2; note.maxLength = 500;
        note.value = photo.note; note.placeholder = 'For example: use the dog on the left.';
        note.disabled = this.state.busy;
        note.addEventListener('input', () => { photo.note = note.value; }); label.append(note);
        info.append(name, label);
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'ws-native-photo__remove';
        remove.textContent = 'Remove'; remove.setAttribute('aria-label', `Remove photo ${index + 1}: ${photo.file.name}`);
        remove.disabled = this.state.busy;
        remove.addEventListener('click', () => {
          URL.revokeObjectURL(photo.url); this.state.photos.splice(index, 1); this.showError(''); this.render(); this.button.focus();
        });
        card.append(image, info, remove); this.list.append(card);
      });
      this.button.disabled = this.state.busy || this.state.photos.length === MAX_FILES;
      this.button.textContent = this.state.photos.length ? 'Add more photos' : 'Upload photos';
      this.setAttribute('aria-busy', String(this.state.busy));
      this.status.textContent = this.state.busy ? 'Uploading photos and adding your socks… Please keep this page open.' :
        this.state.photos.length ? `${this.state.photos.length} of 4 photos selected. Originals will upload when you add to cart.` : 'Up to 4 photos per pair · JPG, PNG, WebP · 10 MB each';
    }

    async submit(event) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.state.busy) return;
      const localPreview = ['127.0.0.1', 'localhost', '[::1]'].includes(location.hostname);
      const privateProxy = this.dataset.privateProxy;
      // Fail closed until the protected app proxy has actually been configured.
      if (!localPreview && !/^\/apps\/[a-z0-9_-]+\/photos$/.test(privateProxy || '')) {
        this.showError('Photo upload is not connected yet. Please contact the store for help.');
        return;
      }
      if (!this.state.photos.length) {
        this.showError('Please add at least one pet photo before adding to cart.'); this.button.focus(); return;
      }
      const form = this.form;
      if (!form || !form.reportValidity()) return;
      const variant = form.querySelector('[name="id"]');
      if (!variant?.value || variant.disabled) { this.showError('Please finish choosing an available color and sock length.'); return; }
      const data = new FormData(form);
      const quantityAtSubmit = Number(data.get('quantity') || 1);
      if (!Number.isSafeInteger(quantityAtSubmit) || quantityAtSubmit < 1) {
        this.showError('Please choose a valid number of pairs.'); return;
      }
      // A native module must not submit old app URLs together with the chosen files.
      for (const name of [...data.keys()]) {
        if (/^properties\[(Your Photo(?: \d+)?|Notes:\d+|_ws_photo_set|_ws_photo_receipt|Pet photos)\]$/.test(name)) data.delete(name);
      }
      // Unique grouping prevents two separate customizations merging into one cart line.
      data.set('properties[_ws_photo_set]', crypto.randomUUID());
      const component = form.closest('product-form-component');
      if (component?.dataset.wsVariantPending === 'true') {
        this.showError('Please wait for your color and sock length to finish updating, then add to cart.'); return;
      }
      const variantAtSubmit = variant.value;
      const controls = [...form.querySelectorAll('button[type="submit"], input[type="submit"]')];
      const disabled = controls.map(control => control.disabled);
      controls.forEach(control => { control.disabled = true; });
      this.state.busy = true; this.showError(''); this.render();
      try {
        if (privateProxy) {
          const uploadData = new FormData();
          const cartResponse=await fetch('/cart.js',{headers:{Accept:'application/json'},cache:'no-store'});
          if(!cartResponse.ok)throw new Error('Your cart could not be verified. Please try again.');
          const verifiedCart=await cartResponse.json();
          if(typeof verifiedCart.token!=='string' || verifiedCart.token.length<16)throw new Error('Your cart could not be verified. Please try again.');
          uploadData.append('cart_key',verifiedCart.token);
          this.state.photos.forEach(photo => uploadData.append('photos', photo.file, photo.file.name));
          const nonce = [...crypto.getRandomValues(new Uint8Array(24))].map(value => value.toString(16).padStart(2, '0')).join('');
          const uploadedResponse = await fetch(`${privateProxy}?nonce=${nonce}`, { method: 'POST', body: uploadData, headers: { Accept: 'application/json' } });
          const uploaded = await uploadedResponse.json();
          if (!uploadedResponse.ok) throw new Error(uploaded.error || 'The photo upload failed. Please try again.');
          if (!Array.isArray(uploaded.photos) || uploaded.photos.length !== this.state.photos.length || typeof uploaded.receipt !== 'string') throw new Error('The photo upload could not be confirmed.');
          if (uploaded.photos.some(photo => !/^[a-f0-9]{48}$/.test(photo.id))) throw new Error('The photo upload could not be confirmed.');
          data.set('properties[_ws_photo_receipt]', uploaded.receipt);
          data.set('properties[Pet photos]', `${uploaded.photos.length} photo${uploaded.photos.length === 1 ? '' : 's'} attached`);
        } else {
          // Legacy transport is available only to the local test server.
          this.state.photos.forEach((photo, index) => data.set(`properties[Your Photo ${index + 1}]`, photo.file, photo.file.name));
        }
        this.state.photos.forEach((photo, index) => { if (photo.note.trim()) data.set(`properties[Notes:${index + 1}]`, photo.note.trim()); });
        const latestForm = this.form;
        if (!latestForm || latestForm.querySelector('[name="id"]')?.value !== variantAtSubmit || latestForm.querySelector('[name="id"]')?.disabled || latestForm.closest('product-form-component')?.dataset.wsVariantPending === 'true' || Number(new FormData(latestForm).get('quantity') || 1) !== quantityAtSubmit) throw new Error('Your options changed while the photos were uploading. Review the color, sock length and quantity, then try again.');
        const action = new URL(form.action, location.href);
        action.pathname = action.pathname.replace(/\/add\/?$/, '/add.js');
        if (action.origin !== location.origin) throw new Error('Unexpected cart destination. Please reload this page.');
        const response = await fetch(action, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Your socks could not be added. Please try again.');
        const item = result.items?.[0] || result;
        // Confirm the private binding (or links in the localhost-only legacy preview).
        const attachmentConfirmed = privateProxy
          ? item.properties?._ws_photo_receipt === data.get('properties[_ws_photo_receipt]')
          : this.state.photos.every((_, index) => typeof item.properties?.[`Your Photo ${index + 1}`] === 'string' && /^https?:\/\//i.test(item.properties[`Your Photo ${index + 1}`]));
        if (!attachmentConfirmed) {
          this.showError('We could not confirm the photo links. Please check your cart before adding this item again.');
          this.current.querySelector('[data-photo-cart-link]').hidden = false;
          return;
        }
        // Native cart flow: one successful add, one navigation; no duplicated analytics purchase events.
        location.assign(this.dataset.cartUrl || '/cart');
      } catch (error) {
        // A failed response can be ambiguous. Keep the files and provide a cart check before retrying.
        this.showError(`${error.message || 'The connection was interrupted.'} Your selected photos are still here. Check your cart before retrying.`);
        this.current.querySelector('[data-photo-cart-link]').hidden = false;
      } finally {
        this.state.busy = false; this.current.render();
        controls.forEach((control, index) => { if (control.isConnected) control.disabled = disabled[index]; });
      }
    }
  }
  customElements.define('woofsocks-photo-upload', PhotoUpload);
  // Capture before theme queue / Uploadly validation. Install once, including after section morphs.
  window.addEventListener('submit', event => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const module = [...document.querySelectorAll('woofsocks-photo-upload')].find(element => element.form === event.target);
    if (module) module.submit(event);
  }, true);
})();
